import { z } from "zod";
import { Schedule } from "@visioni/schedules";
import { AlarmFeatureName, AlarmFeatureState } from "./types";

/**
 * Cloud-internal alarm feature names. These features are not part of the
 * public {@link AlarmFeatureName} contract — they exist only inside the cloud
 * to drive internal behaviours (publishing the alarm to downstream consumers,
 * uploading the activation clip). The edge tolerates them on the wire because
 * {@link AlarmSchedule} strips unknown keys; once the edge is upgraded to
 * parse with {@link InternalAlarmSchedule} it will preserve them.
 */
export const InternalOnlyAlarmFeatureName = z.enum(["publish_alarm", "upload_clip"]);
export type InternalOnlyAlarmFeatureName = z.infer<typeof InternalOnlyAlarmFeatureName>;

/**
 * The full feature-name enum used inside the cloud — the four public alarm
 * features plus the internal-only extension features. The four originals
 * remain required on every parsed schedule (see {@link InternalAlarmSchedule}
 * preprocess); the two extension keys are effectively optional because the
 * preprocess fills them in with default {@link AlarmFeature} definitions
 * when callers don't supply them.
 *
 * The public {@link AlarmFeatureName} enum is intentionally left at four
 * members so the public API surface is unaffected.
 */
export const InternalAlarmFeatureName = z.enum([...AlarmFeatureName.options, ...InternalOnlyAlarmFeatureName.options]);
export type InternalAlarmFeatureName = z.infer<typeof InternalAlarmFeatureName>;

/**
 * Default `Feature` definition injected for each internal-only feature when
 * the parsed schedule omits it.
 *
 * - `default_state: "disarmed"` mirrors the four public features: a feature
 *   is considered off whenever no rule says otherwise.
 * - `allowed_states: ["armed", "disarmed"]` matches the public alarm states
 *   so rules can flip the feature on or off in the same way.
 *
 * The "armed/on by default" semantic the plan calls for is delivered at the
 * `AlarmResponseToggle` layer (Phase 3 will inject `"on"` into the response),
 * not at the schedule's `default_state`. Keeping `default_state` as
 * `disarmed` here means a schedule with no rules at all leaves the new
 * features off — the same baseline behaviour as the existing four.
 */
const INTERNAL_ALARM_FEATURE_DEFAULT = {
  default_state: "disarmed",
  allowed_states: ["armed", "disarmed"],
} as const;

const INTERNAL_ALARM_FEATURE_DEFAULTS: Readonly<
  Record<InternalOnlyAlarmFeatureName, typeof INTERNAL_ALARM_FEATURE_DEFAULT>
> = Object.freeze({
  publish_alarm: INTERNAL_ALARM_FEATURE_DEFAULT,
  upload_clip: INTERNAL_ALARM_FEATURE_DEFAULT,
});

/**
 * Cloud-internal alarm schedule schema — a strict superset of
 * {@link AlarmSchedule} that recognises the two internal-only features as
 * extension keys.
 *
 * Design intent (per the
 * `2026-05-29-alarm-internal-publish-upload-features` plan):
 *
 * - The public schemas (`AlarmFeatureName`, `AlarmSchedule`, `AlarmResponse`,
 *   `Features`, `Schedule`) stay exactly as they are. This schema is the
 *   *extension*, not a replacement.
 * - The four existing alarm features (`monitoring`, `recording`, `sirens`,
 *   `strobes`) remain mandatory on every parsed schedule. Dropping any of
 *   them is still a parse error.
 * - The two internal-only features (`publish_alarm`, `upload_clip`) are
 *   *optional from the caller's perspective*: if absent, the preprocess
 *   below injects a default `Feature` definition so the strict
 *   `Schedule(InternalAlarmFeatureName, …)` validation succeeds. If
 *   present, the caller's value wins.
 * - Unknown keys outside the six-member enum are silently stripped by the
 *   underlying `Features()` codec — the same behaviour the public
 *   `AlarmSchedule` already has for old/legacy keys.
 *
 * Rules (`rule.features`) accept any of the six keys via the wider
 * `partialRecord` produced by `Schedule(InternalAlarmFeatureName, …)`. The
 * existing public `AlarmSchedule` rejects keys outside its four-key enum,
 * which is why the edge must be upgraded to parse with
 * `InternalAlarmSchedule` before the cloud starts emitting schedules whose
 * rules mention the internal-only features.
 */
export const InternalAlarmSchedule = z.preprocess(
  (raw) => {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return raw;
    const record = raw as Record<string, unknown>;
    const features = record["features"];
    if (typeof features !== "object" || features === null || Array.isArray(features)) {
      return raw;
    }
    return {
      ...record,
      features: {
        ...INTERNAL_ALARM_FEATURE_DEFAULTS,
        ...(features as Record<string, unknown>),
      },
    };
  },
  Schedule(InternalAlarmFeatureName, AlarmFeatureState),
);
export type InternalAlarmSchedule = z.infer<typeof InternalAlarmSchedule>;
